import { getTransactionAnalytics } from '@ynap/analytics'

const primaryCategory = 'checkout'

// Basket
export const trackViewMiniPDP = ({ checkoutAnalytics }, partNumber) => {
    const product = checkoutAnalytics?.lineItems?.find((item) => item.partNumber === partNumber)
    const { productId, category, price, firstVisibleDate, analyticsAttributes } = product || {}

    const item = product
        ? [
              {
                  productInfo: {
                      productID: productId,
                      sku: partNumber,
                  },
                  category: {
                      primaryCategory: analyticsAttributes?.category?.primaryCategory || category.primaryCategory,
                      subCategory1: analyticsAttributes?.category?.subCategory1 || category.subCategory1,
                      subCategory2: analyticsAttributes?.category?.subCategory2 || category.subCategory2,
                  },
                  ...(firstVisibleDate
                      ? {
                            attributes: {
                                firstVisibleDate,
                            },
                        }
                      : {}),
                  price: {
                      currency: price.currency?.toLowerCase(),
                      basePrice: price.xitem_baseOrderItemPrice || price.rawUnitPrice,
                      fullPrice: price.fullPrice,
                  },
              },
          ]
        : []

    return {
        event: 'quick view product - from shopping bag',
        eventEffect: 'mini-view',
        category: {
            primaryCategory,
            subCategory: 'basket',
        },
        item,
    }
}

// Shipping Address Step
export const addNewShippingAddress = ({ checkoutAnalytics }) => ({
    event: 'add_new_shipping_address',
    effect: 'add address',
    category: { primaryCategory, subCategory: 'shipping address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const confirmNewShippingAddress = ({ checkoutAnalytics }) => ({
    event: 'confirm adding address',
    effect: 'add address',
    category: { primaryCategory, subCategory: 'shipping address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const shippingAddressUpdate = ({ checkoutAnalytics }) => ({
    event: 'update of shipping address',
    effect: 'update address',
    category: { primaryCategory, subCategory: 'shipping address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const shippingAddressEdit = ({ checkoutAnalytics }) => ({
    event: 'edit shipping address',
    effect: 'edit address',
    category: { primaryCategory, subCategory: 'shipping address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const addressHasShippingRestriction = ({ checkoutAnalytics }, { defaultAddress }) => ({
    event: 'shipping restrictions apply',
    effect: 'notify user of shipping restrictions',
    category: { primaryCategory, subCategory: 'shipping address' },
    attributes: { restrictedItems: 'all', defaultAddress },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const shippingAddressContinue = ({ checkoutAnalytics }) => ({
    event: 'continue',
    effect: 'continue to next step',
    category: { primaryCategory, subCategory: 'shipping address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const shippingAddressContinueError = ({ checkoutAnalytics }) => ({
    event: 'continue error',
    effect: 'continue error',
    category: { primaryCategory, subCategory: 'shipping address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

// Shipping Options Step

export const shippingMethodSelected = (checkoutAnalytics, option) => {
    return {
        event: option.nameEN,
        effect: 'shipping options',
        category: { primaryCategory, subCategory: 'shipping options' },
        transaction: getTransactionAnalytics(checkoutAnalytics),
    }
}

export const shippingSignatureOption = (checkoutAnalytics, requiresSignature) => {
    const event = requiresSignature ? 'signature required' : 'signature not required'
    return {
        event,
        effect: 'collection options',
        category: { primaryCategory, subCategory: 'shipping options' },
        transaction: getTransactionAnalytics(checkoutAnalytics),
    }
}

export const shippingMethodPremierPopup = (checkoutAnalytics,shipModeCode) => {
    return {
        event: 'select_premier_shipping_popup',
        position: shipModeCode,
        transaction: getTransactionAnalytics(checkoutAnalytics),
    }
}

// event: 'impression' | 'change to premier' | 'closed'
export const premierShippingNotification = (checkoutAnalytics, event) => ({
    event: `premier shipping notification - ${event || ''}`,
    effect: 'prompt shown to user',
    category: { primaryCategory, subCategory: 'shipping options' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const shippingOptionsContinue = (checkoutAnalytics) => ({
    event: 'continue',
    effect: 'continue to next step',
    category: { primaryCategory, subCategory: 'shipping options' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

// Packaging Step

export const packagingOptionsChange = (checkoutAnalytics, packagingOptionName) => ({
    event: packagingOptionName.toLowerCase() + ' packaging',
    effect: 'packaging option',
    category: { primaryCategory, subCategory: 'packaging and gifting' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const packagingOptionsContinue = ({ checkoutAnalytics }) => ({
    event: 'continue',
    effect: 'continue to next step',
    category: { primaryCategory, subCategory: 'packaging and gifting' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

// Payment Step

export const clickToAddPromoCode = ({ checkoutAnalytics }) => ({
    event: 'add promo code',
    effect: 'click on Add a promo code button',
    category: { primaryCategory, subCategory: 'payment' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const clickToAddGiftCard = ({ checkoutAnalytics }) => ({
    event: 'add giftcard notification',
    effect: 'giftcard notification',
    category: { primaryCategory, subCategory: 'payment' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const addNewBillingAddress = (checkoutAnalytics) => ({
    event: 'add billing address',
    effect: 'add new billing address',
    category: { primaryCategory, subCategory: 'billing address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const billingAddressContinue = (checkoutAnalytics) => ({
    event: 'confirm billing address',
    effect: 'add billing address',
    category: { primaryCategory, subCategory: 'billing address' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

// ApplePay

export const trackProceedToPurchaseApplePay = (checkoutAnalytics) => ({
    event: 'proceed to purchase - apple pay',
    effect: 'move to checkout',
    category: { primaryCategory, subCategory: 'payment' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const trackCancelApplePay = (checkoutAnalytics) => ({
    event: 'apple pay - cancel',
    effect: 'close apple pay',
    category: { primaryCategory, subCategory: 'payment' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const trackErrorApplePay = (checkoutAnalytics, error) => ({
    event: `error - ${error.message}`,
    effect: 'error',
    category: {
        subCategory: 'apple pay',
        primaryCategory: 'error message',
    },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const trackApplePaySetup = (checkoutAnalytics) => ({
    event: `apple pay - setup`,
    effect: 'display setup apple pay prompt',
    category: { primaryCategory, subCategory: 'payment' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

// Order Review Step

export const trackBuyNow = ({ checkoutAnalytics }) => ({
    event: 'buy now',
    effect: 'buy now',
    category: { primaryCategory, subCategory: 'payment' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

export const trackReviewOrder = ({ checkoutAnalytics }) => ({
    event: 'review and order',
    effect: 'review order',
    category: { primaryCategory, subCategory: 'payment' },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})

// Order Confirmation

export const trackConsideredLinkClick = (checkoutAnalytics, targetURL) => ({
    event: 'order confirmation - link click',
    effect: 'click on a link inside the constidered info section',
    category: { primaryCategory: 'product page', subCategory: targetURL },
    transaction: getTransactionAnalytics(checkoutAnalytics),
})
